/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOAST_MSG_UPDATE_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Updated Toastify messages to Irit's improved user-friendly versions.
 *
 * File Size Check:
 * - Current: 118 lines
 * - Adding: ~6 lines
 * - Decision: Proceed
 *
 * Typography Compliance: N/A (notification messages only)
 * Related Files: utils/notifications.ts
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    UI_STANDARDIZATION
 * Date:         2025-12-07
 * Modified By:  Cursor
 *
 * Description:
 * Standardize toast usage via centralized notifications helper.
 * Enforce consistent UI messaging style and error sanitization.
 *
 * Related Screens / Modules:
 * - client/src/utils/notifications.ts
 * - client/src/features/setup/alerts/MyAlertProfilesPage.tsx
 */

import React, { useState, useEffect } from "react";
import { Container, Row, Col, Breadcrumb } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { notify } from "../../../utils/notifications";
import ProfileCreationCards from "./ProfileCreationCards";
import ProfilesList from "./ProfilesList";
import { AlertProfile, duplicateAlertProfile } from "../../../services/alertProfilesService";

const MyAlertProfilesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Check for refresh parameter and trigger refresh
  useEffect(() => {
    const refresh = searchParams.get("refresh");
    if (refresh === "true") {
      setRefreshTrigger((prev) => prev + 1);
      // Remove the refresh parameter from URL
      searchParams.delete("refresh");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSmartCreate = () => {
    navigate("/alerts/smart/new");
  };

  const handleEdit = (profile: AlertProfile) => {
    const editPath =
      profile.mode === "smart"
        ? `/alerts/smart/edit/${profile._id}`
        : `/alerts/professional/edit/${profile._id}`;
    navigate(editPath);
  };

  const handleDuplicate = async (profile: AlertProfile) => {
    try {
      const response = await duplicateAlertProfile(profile._id);
      if (response.success) {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.success(`Profile "${profile.name}" duplicated successfully`);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
        notify.error(null, "We couldn't duplicate this profile. Please try again");
      }
    } catch (err) {
      // [2026-01-27] TOAST_MSG_UPDATE_IRIT_2026: Updated to user-friendly message
      notify.error(err, "We couldn't duplicate this profile. Please try again");
      console.error("Error duplicating profile:", err);
    }
  };

  return (
    <Container fluid className="my-alert-profiles-container">
      {/* Breadcrumb Navigation */}
      <Row className="mb-3">
        <Col>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item active>Broadcast Alerts</Breadcrumb.Item>
          </Breadcrumb>
        </Col>
      </Row>

      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <div className="text-center">
            <h2 className="mb-3 my-alert-profiles-title">Broadcast Alerts</h2>
            <p className="text-muted my-alert-profiles-subtitle">
              Create and manage your custom alert profiles to stay updated on relevant inventory.
            </p>
          </div>
        </Col>
      </Row>

      {/* Profile Creation Cards */}
      <ProfileCreationCards
        onSmartCreate={handleSmartCreate}
      />

      {/* Divider */}
      <Row className="mb-4">
        <Col>
          <hr className="my-alert-profiles-divider" />
        </Col>
      </Row>

      {/* Existing Profiles List */}
      <ProfilesList
        onEdit={handleEdit}
        onDuplicate={handleDuplicate}
        refreshTrigger={refreshTrigger}
      />
    </Container>
  );
};

export default MyAlertProfilesPage;
